import { FC, PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TabPanes, Topbar, PageLoader, toast } from "@vilocnv/allsetra-core";
import { Box, Stack, useTheme } from "@mui/material";
import { Add, Settings } from "@mui/icons-material";
import { PageLoaderCentered } from "components/sections/objects/ObjectDetailsSection/ObjectDetailsSection.styled";
import AddMaintenanceAlertRuleModal from "./AddMaintenanceAlertRuleModal";
import AddManualRideForm from "components/forms/objects/AddManualRideForm/AddManualRideForm";
import { MAINTENANCE_ALERT_RULES_UPDATED_EVENT } from "components/common/objects/MaintenanceAlerts/maintenanceAlertsEvents";

// Data
import {
  useActiveObjectById,
  useAppDispatch,
  useAppSelector,
  useCurrentUser
} from "hooks";
import { getObjectFeaturesThunk, setObjectActiveTabIndex } from "app/features";
import {
  selectDrawerSelectedAccountId,
  selectIsDrawerCollapsed,
  selectObjectActiveTabIndex,
  selectObjectFeaturesState
} from "app/data/selectors";
import {
  getObjectsDetailsTabRouteNames,
  OBJECTS_DETAILS_TABS_HEADINGS
} from "app/data/constants";
import { useTranslation } from "react-i18next";
import InactiveObjectBadge from "components/ui/InactiveObjectBadge";
import { isInactiveObject, isMonitoringTabEnabled } from "app/data/helpers";
import { analyticsEnabled } from "app/data/services/AnalyticsSession";

const ObjectTabsLayout: FC<PropsWithChildren> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isAdmin } = useCurrentUser();

  // Global State
  const isDrawerCollapsed = useAppSelector(selectIsDrawerCollapsed);
  const objectActiveTabIndex = useAppSelector(selectObjectActiveTabIndex);
  const drawerSelectedAccountId = useAppSelector(selectDrawerSelectedAccountId);
  const isMonitoringEnabled = isMonitoringTabEnabled(drawerSelectedAccountId);
  const isGrafanaEnabled = isMonitoringEnabled && analyticsEnabled;
  const { objectFeaturesConfig, objectFeaturesLoading } = useAppSelector(
    selectObjectFeaturesState
  );

  const { specificObject, loading } = useActiveObjectById();

  const { t } = useTranslation();
  const isMaintenanceAlertsTab =
    location.pathname.includes("maintenance-alerts");
  const isRidesTab = location.pathname.includes("rides");
  const [isAddAlertRuleModalOpen, setIsAddAlertRuleModalOpen] = useState(false);
  const [isAddManualRideModalOpen, setIsAddManualRideModalOpen] =
    useState(false);

  const tabsHeadings = useMemo(
    () =>
      OBJECTS_DETAILS_TABS_HEADINGS(
        t,
        objectFeaturesConfig,
        isMonitoringEnabled,
        isGrafanaEnabled
      ),
    [t, objectFeaturesConfig, isMonitoringEnabled, isGrafanaEnabled]
  );

  const tabRouteNames = useMemo(
    () =>
      getObjectsDetailsTabRouteNames(
        objectFeaturesConfig.isRidesEnabled,
        isMonitoringEnabled,
        isGrafanaEnabled
      ),
    [objectFeaturesConfig.isRidesEnabled, isMonitoringEnabled, isGrafanaEnabled]
  );

  useEffect(() => {
    const nextIndex = tabRouteNames.findIndex((routeName: string) =>
      location.pathname.includes(routeName)
    );

    if (nextIndex !== -1) {
      dispatch(setObjectActiveTabIndex(nextIndex));
    }
  }, [location.pathname, tabRouteNames, dispatch]);

  useEffect(() => {
    const fetchSpecificObjectFeatures = async () => {
      const { type } = await dispatch(
        getObjectFeaturesThunk({
          accountId: drawerSelectedAccountId ?? "",
          objectId: params.id ?? ""
        })
      );

      if (type === "objects/getObjectFeaturesThunk/rejected") {
        navigate(-1);
        toast.error("Object features were not found.");
      }
    };

    fetchSpecificObjectFeatures();
  }, [drawerSelectedAccountId, params.id, dispatch, navigate]);

  const onChangeTab = (value: number) => {
    dispatch(setObjectActiveTabIndex(value));
    const routeName = tabRouteNames[value] || "details";
    navigate(`/dashboard/objects/${routeName}/${params.id}`);
  };

  const secondaryButton = (() => {
    if (isMaintenanceAlertsTab) {
      return {
        id: "add-maintenance-alert-rule",
        variant: "contained" as const,
        text: t("maintenanceAlerts.addAlertRule"),
        onClick: () => setIsAddAlertRuleModalOpen(true),
        startIcon: <Add />,
        sx: {
          backgroundColor: "#00B2A3",
          color: "#FFFFFF",
          marginLeft: "10px"
        }
      };
    }

    if (isRidesTab && isAdmin) {
      return {
        id: "add-manual-ride",
        variant: "contained" as const,
        text: t("buttonsText.addManualRide"),
        onClick: () => setIsAddManualRideModalOpen(true),
        startIcon: <Add />,
        sx: {
          backgroundColor: "#00B2A3",
          color: "#FFFFFF",
          marginLeft: "10px"
        }
      };
    }

    return undefined;
  })();

  return (
    <Box>
      {loading || objectFeaturesLoading ? (
        <PageLoaderCentered isDrawerCollapsed={isDrawerCollapsed}>
          <PageLoader />
        </PageLoaderCentered>
      ) : (
        <Box>
          <Topbar
            theme={theme}
            title={specificObject?.name || "Object"}
            breadcrumbTitle={t("drawerMenuLinks.objects")}
            breadcrumbRedirectTo={() => navigate("/dashboard/objects")}
            primaryButton={{
              id: "object-settings",
              variant: "outlined",
              text: t("buttonsText.manageObject"),
              onClick: () =>
                navigate(
                  `/dashboard/objects/${specificObject?.uniqueId}/settings`
                ),
              startIcon: <Settings />,
              sx: {
                border: "1px solid #00B2A3 !important"
              }
            }}
            secondaryButton={secondaryButton}
          />
          {isInactiveObject(specificObject?.status) ? (
            <Box px={3} pb={1}>
              <Stack direction="row" alignItems="center">
                <InactiveObjectBadge variant="inline" />
              </Stack>
            </Box>
          ) : null}
          <Box
            sx={{
              "& .MuiTabs-root": {
                "& .MuiTab-root.Mui-selected": {
                  color: "#845AFC"
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#845AFC"
                }
              }
            }}
          >
            <TabPanes
              value={objectActiveTabIndex}
              onChange={onChangeTab}
              headings={tabsHeadings}
            >
              <Box mx={2}>{children}</Box>
            </TabPanes>
          </Box>
        </Box>
      )}
      <AddMaintenanceAlertRuleModal
        open={isAddAlertRuleModalOpen}
        onClose={() => setIsAddAlertRuleModalOpen(false)}
        accountId={drawerSelectedAccountId || ""}
        defaultObject={
          specificObject
            ? { uniqueId: specificObject.uniqueId, name: specificObject.name }
            : null
        }
        onSuccess={(result) => {
          window.dispatchEvent(
            new CustomEvent(MAINTENANCE_ALERT_RULES_UPDATED_EVENT, {
              detail: result
            })
          );
        }}
      />
      {specificObject?.uniqueId && (
        <AddManualRideForm
          open={isAddManualRideModalOpen}
          onClose={() => setIsAddManualRideModalOpen(false)}
          accountId={drawerSelectedAccountId || ""}
          objectId={specificObject.uniqueId}
        />
      )}
    </Box>
  );
};

export default ObjectTabsLayout;
